import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalService } from 'app/global/global.service';
import { IDocumentDetailRootDTO, DocumentInternalReminder } from 'app/main/pages/documents/dto/document-details.dto';
import { DocumentsService } from 'app/main/pages/documents/documents.service';
import { SharedStoreFacade } from 'app/main/shared/shared/store/shared-store.facade';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import * as moment from 'moment';

@Component({
  selector: 'app-internal-reminder-modal',
  templateUrl: './internal-reminder-modal.component.html',
  styleUrl: './internal-reminder-modal.component.scss'
})
export class InternalReminderModalComponent implements OnInit {

  @Input() documentData: IDocumentDetailRootDTO;

  public internalReminderFormSubmitted: boolean = false;
  public internalReminderLoading: boolean = false;
  public internalReminderForm: FormGroup;
  public users$ = this._sharedStoreFacade.users$;
  public reminders: DocumentInternalReminder[] = [];
  public editingReminderId: number | null = null;
  public minDate: Date | null = new Date();

  constructor (
    private _formBuilder: FormBuilder,
    private _globalService: GlobalService,
    private _documentService: DocumentsService,
    private _activeModal$: NgbActiveModal,
    private _sharedStoreFacade: SharedStoreFacade,
    private _translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.initInternalReminderForm();
    this._sharedStoreFacade.fetchSubUsers();
    if (this.documentData?.document_internal_reminder) {
      // Clone reminder list because store-provided arrays can be frozen (non-extensible).
      const incomingReminders = Array.isArray(this.documentData.document_internal_reminder)
        ? this.documentData.document_internal_reminder
        : [this.documentData.document_internal_reminder];
      this.reminders = this.cloneReminders(incomingReminders);
    }
  }


  /**
   * Initialize internal reminder form
   */
  initInternalReminderForm(): void {
    this.internalReminderForm = this._formBuilder.group({
      reminder_datetime: [null, [Validators.required]],
      message: [null, [Validators.required]],
      user_ids: [[], [Validators.required]]
    });
  }


  /**
   * Reminder Form controls
   */
  get reminderFormControls() {
    return this.internalReminderForm.controls;
  }



  /**
   * Close modal
   */
  closeModal(): void {
    this._activeModal$.close({ document_internal_reminder: this.reminders });
  }


  
  /**
   * Set reminder
   * @returns undefined | null
   */
  setReminder(): void {
    this.internalReminderFormSubmitted = true;
    if (this.internalReminderForm.invalid) {
      return;
    }
    this.internalReminderLoading = true;
    const payload = { ...this.internalReminderForm.value };
    const documentId = this.documentData?.template?.id;

    if (this.editingReminderId) {
      this.updateReminder(documentId, this.editingReminderId, payload);
      return;
    }

    this._documentService.setReminder(documentId, payload)
      .pipe(finalize(() => {
        this.internalReminderFormSubmitted = false;
        this.internalReminderLoading = false;
      }))
      .subscribe({
        next: (response) => {
          this.resetFormState();
          this.reminders = [...this.reminders, this.cloneReminder(response)];
          this._globalService.toastrSuccess(this._translocoService.translate('Internal reminder success toast (Documents Section) - Reminder set successfully'));
        },
        error: (error: HttpErrorResponse) => {
          this._globalService.toastrError(error.error.message ?? this._translocoService.translate('Internal reminder error toast (Documents Section) - Failed to set reminder'));
        }
      });
  }

  editReminder(reminder: DocumentInternalReminder): void {
    this.editingReminderId = reminder.id;
    // Allow editing historical reminders without flatpickr clearing old value.
    this.minDate = null;
    this.internalReminderForm.patchValue({
      reminder_datetime: this.parseReminderDate(reminder.reminder_datetime),
      message: reminder.message,
      user_ids: (reminder.users || []).map(user => user.id)
    });
    this.internalReminderFormSubmitted = false;
  }

  cancelEditReminder(): void {
    this.resetFormState();
  }

  isReminderPassed(reminder: DocumentInternalReminder): boolean {
    return new Date(reminder?.reminder_datetime).getTime() < Date.now();
  }

  get activeReminders(): DocumentInternalReminder[] {
    return this.sortReminders(this.reminders.filter(reminder => !this.isReminderPassed(reminder)));
  }

  get passedReminders(): DocumentInternalReminder[] {
    return this.sortReminders(this.reminders.filter(reminder => this.isReminderPassed(reminder)));
  }

  get pendingCount(): number {
    return this.activeReminders.length;
  }

  setQuickReminder(hours: number): void {
    const date = new Date();
    date.setHours(date.getHours() + hours);
    this.internalReminderForm.patchValue({ reminder_datetime: date });
  }

  setTomorrowReminder(): void {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    date.setHours(9, 0, 0, 0);
    this.internalReminderForm.patchValue({ reminder_datetime: date });
  }
  
  deleteReminder(id: number): void {
      this._documentService.deleteInternalReminder(this.documentData?.template?.id, id).subscribe({
          next: () => {
              this.reminders = this.reminders.filter(r => r.id !== id);
              if (this.editingReminderId === id) {
                this.resetFormState();
              }
              this._globalService.toastrSuccess(this._translocoService.translate('Internal reminder success toast (Documents Section) - Reminder deleted successfully'));
          },
          error: (error) => {
              this._globalService.toastrError(error.error.message ?? this._translocoService.translate('Internal reminder error toast (Documents Section) - Failed to delete reminder'));
          }
      });
  }

  private updateReminder(documentId: number, reminderId: number, payload: { reminder_datetime: string; message: string; user_ids: number[] }): void {
    this._documentService.setReminder(documentId, { ...payload, id: reminderId })
      .pipe(finalize(() => {
        this.internalReminderFormSubmitted = false;
        this.internalReminderLoading = false;
      }))
      .subscribe({
        next: (response) => {
          const clonedResponse = this.cloneReminder(response);
          this.reminders = this.reminders.map(reminder => reminder.id === reminderId ? clonedResponse : reminder);
          this.resetFormState();
          this._globalService.toastrSuccess(this._translocoService.translate('Internal reminder success toast (Documents Section) - Reminder updated successfully'));
        },
        error: (error: HttpErrorResponse) => {
          this._globalService.toastrError(error.error?.message ?? this._translocoService.translate('Internal reminder error toast (Documents Section) - Failed to update reminder'));
        }
      });
  }

  private sortReminders(reminders: DocumentInternalReminder[]): DocumentInternalReminder[] {
    return [...reminders].sort((a, b) => new Date(a.reminder_datetime).getTime() - new Date(b.reminder_datetime).getTime());
  }

  private resetFormState(): void {
    this.editingReminderId = null;
    this.minDate = new Date();
    this.internalReminderForm.reset({
      reminder_datetime: null,
      message: null,
      user_ids: []
    });
  }

  private parseReminderDate(value: string): Date | null {
    if (!value) {
      return null;
    }

    const withExpectedFormat = moment(value, 'YYYY-MM-DD HH:mm', true);
    if (withExpectedFormat.isValid()) {
      return withExpectedFormat.toDate();
    }

    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  private cloneReminders(reminders: DocumentInternalReminder[]): DocumentInternalReminder[] {
    return (reminders || []).map(reminder => this.cloneReminder(reminder));
  }

  private cloneReminder(reminder: DocumentInternalReminder): DocumentInternalReminder {
    if (!reminder) {
      return reminder;
    }

    return {
      ...reminder,
      users: [...(reminder.users || [])]
    };
  }
}
